import { useEffect, useMemo } from "react";
import { NavLink } from "react-router";
import { Checkbox, Icon, Loader } from "semantic-ui-react";
import { connect, type ConnectedProps } from "react-redux";

import Tooltip from "../../../../components/common/tooltip/Tooltip";
import { helpCenterLink, threatDefenseSettingTooltip } from "../../constants";
import type { AppDispatch, RootState } from "../../../Application/globaltypes/redux";
import { threatDefenceSettingsSelector } from "./state/slices/threatDefenceSettingsSlice";
import {
  deleteThreatDefenceSettings,
  fetchThreatDefenceSettings,
  upsertThreatDefenceSettings,
} from "./state/thunks/threatDefenceSettingsThunk";

import { RolePermissions } from "../../../../enums";
import { permissionsToAccountSelector } from "../state/slices/permissionsToAccountSlice";
import { Typography } from "../../../../components/common/Typography/Typography";

import styles from "./ThreatDefense.module.scss";
import { useFeatureFlag } from "../../../../hooks/useFeatureFlag";
import { FeatureFlags } from "../../../../featureFlags";

interface ThreatDefenseProps {
  accountId: number;
}
export type ThreatDefenseComponentProps = ThreatDefenseProps & PropsFromRedux;

export const ThreatDefense = (props: ThreatDefenseComponentProps) => {
  const isThreatDefenceDomainEnabled = useFeatureFlag(FeatureFlags.ThreatDefenceDomains);
  const { getThreatDefenceSettings } = props;

  const canEdit = useMemo(
    () => props.accountOnEditPermissions.includes(RolePermissions.AccountsSettingsManage),
    [props.accountOnEditPermissions],
  );

  const toggleMsGraphConfiguration = async () => {
    if (!props.threatDefenceSettings.hasMsGraphSettings) {
      await props.upsertThreatDefenceSettings();
    } else {
      await props.deleteThreatDefenceSettings();
    }
  };

  useEffect(() => {
    getThreatDefenceSettings();
  }, [getThreatDefenceSettings]);

  if (props.isLoading) return <Loader role="progressbar" active />;

  return (
    <div className={styles.root}>
      {isThreatDefenceDomainEnabled && (
        <section>
          <Typography variant="title2" bold>
            ASSIGNED DOMAIN
          </Typography>
          <Typography variant="title2">
            This is the assigned domain for your account:{" "}
            <Typography variant="title2" bold component={"span"}>
              {props.accountDomain?.domainName || "-"}.{" "}
            </Typography>
            This is the domain that all campaign sends will originate from.
          </Typography>
        </section>
      )}
      <section>
        <Typography variant="title2" bold>
          SETTINGS
        </Typography>
        <div className={styles["system-notification-setting"]}>
          <Checkbox
            toggle
            checked={props.threatDefenceSettings.hasMsGraphSettings}
            disabled={!canEdit || props.isThreatDefenceSettingsLoading}
            label={"Microsoft Graph Direct Message"}
            onChange={toggleMsGraphConfiguration}
            data-testid={"ms-graph-configuration"}
            className={styles["checkbox-style"]}
          />
          <Tooltip
            tooltipClassName={styles["system-notification-setting__tooltip_threat-defence"]}
            className={styles["system-notification-setting__tooltip-target"]}
            target={<Icon className="info circle" />}
            content={threatDefenseSettingTooltip}
            position="right center"
            hoverable
            hideOnScroll
            showAlways
          />
        </div>
        <Typography variant="paragraph" italic>
          <Typography bold component={"span"}>
            Please Note:
          </Typography>{" "}
          This must be completed before sending simulated phishing campaigns to your organization.
        </Typography>
      </section>

      {isThreatDefenceDomainEnabled && (
        <section>
          <Typography variant="title2" bold>
            WHITE LIST YOUR DOMAIN
          </Typography>
          <div className={styles["white-list-block"]}>
            <Typography variant="title2" component={"span"}>
              Follow the instructions in our{" "}
              <NavLink target="_blank" className={styles["link"]} to={helpCenterLink}>
                Help Center{" "}
              </NavLink>
              to whitelist your domain{" "}
              <Typography variant="title2" bold component={"span"}>
                {props.accountDomain?.domainName || "-"}.
              </Typography>
            </Typography>
            <Typography variant="paragraph" italic>
              <Typography bold component={"span"}>
                Please Note:
              </Typography>{" "}
              This must be completed before sending simulated phishing campaigns to your organization.
            </Typography>
          </div>
        </section>
      )}
    </div>
  );
};

/* istanbul ignore next */
const mapStateToProps = (state: RootState) => {
  const threatDefenceSettings = threatDefenceSettingsSelector(state);
  return {
    accountOnEditPermissions: permissionsToAccountSelector(state),
    threatDefenceSettings: threatDefenceSettings.settings,
    isLoading: threatDefenceSettings.isLoading,
    accountDomain: threatDefenceSettings.accountDomain,
    isThreatDefenceSettingsLoading: threatDefenceSettings.isLoading,
  };
};

/* istanbul ignore next */
const mapDispatchToProps = (dispatch: AppDispatch, ownProps: ThreatDefenseProps) => ({
  getThreatDefenceSettings: () => dispatch(fetchThreatDefenceSettings(ownProps.accountId)),
  upsertThreatDefenceSettings: () => dispatch(upsertThreatDefenceSettings(ownProps.accountId)),
  deleteThreatDefenceSettings: () => dispatch(deleteThreatDefenceSettings(ownProps.accountId)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;

const ConnectedComponent = connector(ThreatDefense);
export default ConnectedComponent;
